/**
 * 买分（top-up）与战报的纯派生。零 React、零 DOM、零 store —— 全部可在 node 环境单测。
 *
 * 单独成文件的理由与 `roomDerive.ts` 一样：这里的规则（入口什么时候出现、
 * 哪一行算「已批准待生效」、战报怎么排版）全是「改 UI 时最容易被顺手改错」的部分。
 *
 * ★ 金额一律由服务端算（红线 #38）。本文件出现的 `allowance` 只是**显示用**的
 *   预估值（起始线 − 当前筹码），上行消息里没有金额字段，真正到账额在生效那一刻重算。
 */
import { ROOM_LIFECYCLE } from '@poker/shared'
import type {
  Chips,
  Millis,
  PlayerView,
  SeatIndex,
  SettlementReport,
  TopUpRequestView,
} from '@poker/shared'

// ============================================================================
// 买分入口
// ============================================================================

/**
 * 筹码低于这么多个大盲才把买分入口显出来。
 *
 * ★ 平时不占位置：牌桌上每一块常驻的 UI 都在和底牌、底池抢那块 390×844 的屏幕。
 *   20bb 是「再打两手就要全下」的公认分界线，这时候玩家才真的需要这颗按钮。
 */
export const TOPUP_LOW_STACK_BB = 20

/**
 * 买分入口的四种形态。
 *
 * ★ `approved` 必须是**独立**的一态而不是「批准即消失」：手牌进行中批准的申请
 *   要排队到手牌边界才到账（红线 #41）。这段等待期玩家看得见，否则他点完申请、
 *   房主点完批准，界面上什么都没变，只会以为没生效而反复申请。
 */
export type TopUpEntryKind = 'hidden' | 'offer' | 'pending' | 'approved'

export interface TopUpEntryModel {
  readonly kind: TopUpEntryKind
  /** 我现在的筹码 */
  readonly stack: Chips
  /** 起始筹码线，也就是能补到的上限 */
  readonly startingStack: Chips
  /** 预估可补额 = 起始线 − 当前筹码（显示用） */
  readonly allowance: Chips
  /** pending / approved 时是那一笔的 id，用于撤回 */
  readonly requestId: string | null
}

const NO_ENTRY: TopUpEntryModel = {
  kind: 'hidden',
  stack: 0,
  startingStack: 0,
  allowance: 0,
  requestId: null,
}

/** 快照里的申请列表。字段为空时服务端**不下发**，所以这里必须兜 undefined。 */
export function topUpsOf(view: PlayerView | null): readonly TopUpRequestView[] {
  return view?.topUps ?? []
}

/** 某个座位当前那一笔申请（服务端保证同一座位同时只有一笔）。 */
export function topUpOfSeat(view: PlayerView | null, seat: SeatIndex | null): TopUpRequestView | null {
  if (seat === null) return null
  return topUpsOf(view).find((r) => r.seat === seat) ?? null
}

/**
 * 我这一侧的买分入口该长什么样。
 *
 * 不出现的四种情况，每一条都对应一次「点了也没用」：
 *   · 没入座 / 没连上 —— 根本没有座位可补；
 *   · 牌局已结算 —— 服务端会回 `E_TOPUP_NOT_ALLOWED`；
 *   · 筹码已达起始线 —— 额度是 0。★ 是**不出现**而不是置灰：一颗永远灰着的按钮
 *     只会让人反复去点，然后怀疑是不是坏了；
 *   · 筹码还很厚 —— 见 `TOPUP_LOW_STACK_BB`。
 *
 * 但只要手上有一笔在途申请，入口一律显示（不看筹码阈值）：那时它承载的是
 * 「等房主批准 / 本手后到账」这条状态，藏起来等于把玩家刚做的事变没。
 */
export function topUpEntry(view: PlayerView | null): TopUpEntryModel {
  if (view === null) return NO_ENTRY
  const seatIndex = view.viewer.ownedSeat
  if (seatIndex === null) return NO_ENTRY
  const seat = view.seats.find((s) => s.index === seatIndex)
  if (seat === undefined || seat.occupant === null) return NO_ENTRY

  const startingStack = view.room.config.startingStack
  const stack = seat.stack
  const allowance = Math.max(0, startingStack - stack)
  const mine = topUpOfSeat(view, seatIndex)
  const base = { stack, startingStack, allowance, requestId: mine?.requestId ?? null }

  if (mine !== null) return { ...base, kind: mine.status === 'approved' ? 'approved' : 'pending' }

  // 结算完（或房间已关）之后只剩战报，没有「再补一笔」这回事
  if (view.settlement !== undefined || view.room.phase === 'closed') return NO_ENTRY
  if (allowance <= 0) return NO_ENTRY
  if (stack >= TOPUP_LOW_STACK_BB * view.room.config.bigBlind) return NO_ENTRY
  return { ...base, kind: 'offer' }
}

/**
 * 等待室抽屉是不是已经替舞台上的买分胶囊说了话。
 *
 * 抽屉的主按钮位置只在「自己暂离、而且筹码用完」时给「买分」或「等房主批准 / 本手后到账」
 * （WaitingRoom 的 heroBroke 就用这一条）。只有这时胶囊才该让位；筹码没用完、只是低于起始线的人，
 * 抽屉里没有任何买分入口 —— 胶囊一藏，他既点不了买分，也看不到自己那笔申请走到哪了、撤不回。
 */
export function waitingRoomOwnsTopUp(view: PlayerView | null): boolean {
  const hero = view?.viewer.ownedSeat ?? null
  if (view === null || hero === null) return false
  const seat = view.seats.find((s) => s.index === hero)
  if (seat === undefined || seat.occupant === null) return false
  return (seat.sitOutNextHand || seat.presence === 'sittingOut') && seat.stack <= 0
}

// ============================================================================
// 房主的审批队列
// ============================================================================

/** 待审批（pending）的申请，先来的排前面。 */
export function pendingTopUps(view: PlayerView | null): readonly TopUpRequestView[] {
  return topUpsOf(view)
    .filter((r) => r.status === 'pending')
    .sort((a, b) => a.requestedAt - b.requestedAt)
}

/** 已批准、等手牌边界到账的申请。 */
export function approvedTopUps(view: PlayerView | null): readonly TopUpRequestView[] {
  return topUpsOf(view)
    .filter((r) => r.status === 'approved')
    .sort((a, b) => a.requestedAt - b.requestedAt)
}

/**
 * 房主角标上的数字。**只数 pending**：已批准的那笔房主已经处理过了，
 * 继续计进角标等于催他再点一次一件已经做完的事。
 */
export function pendingTopUpCount(view: PlayerView | null): number {
  return pendingTopUps(view).length
}

/** 房主要不要看到审批入口。不是房主、或一笔都没有，就完全不出现。 */
export function showTopUpQueue(view: PlayerView | null): boolean {
  if (view === null || !view.viewer.isHost) return false
  return topUpsOf(view).length > 0
}

/**
 * 这笔申请是不是 AI 交的（房主审批列表里挂「AI」标记）。
 * 按座位上的人认：座位号 + 账号都对得上才算，座位换了人的迟到申请不算。
 */
export function topUpIsBot(view: PlayerView | null, req: TopUpRequestView): boolean {
  const occupant = view?.seats.find((s) => s.index === req.seat)?.occupant ?? null
  return occupant !== null && occupant.accountId === req.accountId && occupant.isBot
}

/** 申请人现在有多少筹码（房主审批时要看的第一个数）。 */
export function stackOfSeat(view: PlayerView | null, seat: SeatIndex): Chips {
  return view?.seats.find((s) => s.index === seat)?.stack ?? 0
}

/**
 * 等了多久。房主看的是「这个人被晾了多久」，所以只给到分钟。
 * 不足一分钟说「刚申请」——写「等了 0 分钟」比不写还糟。
 */
export function waitedText(requestedAt: Millis, now: Millis): string {
  const ms = Math.max(0, now - requestedAt)
  const min = Math.floor(ms / 60_000)
  if (min < 1) return '刚申请'
  if (min < 60) return `等了 ${min} 分钟`
  return `等了 ${Math.floor(min / 60)} 小时`
}

/** pending 的申请还剩多久过期（5 分钟无人处理自动作废）。已批准的恒为 null。 */
export function expiresInMs(req: TopUpRequestView, now: Millis): number | null {
  if (req.expiresAt === null) return null
  return Math.max(0, req.expiresAt - now)
}

/** 申请发出那一刻起算的有效期。快照缺 `expiresAt` 时（老数据）用它兜底。 */
export const TOPUP_TTL_MS = ROOM_LIFECYCLE.TOPUP_REQUEST_TTL_MS

// ============================================================================
// 数字格式
// ============================================================================

const GROUP_RE = /\B(?=(\d{3})+(?!\d))/g

/**
 * 战报里的金额一律**完整分组**，不走 `formatChips` 的 K/M 压缩。
 * 一份账要能对得上，「10K」对不出 10,000 还是 10,400。
 */
export function formatExact(n: number): string {
  const neg = n < 0
  const body = String(Math.abs(Math.round(n))).replace(GROUP_RE, ',')
  return neg ? `−${body}` : body
}

/**
 * 净胜负：**符号 + 颜色双重编码**（§11.10）。色弱用户看不出红绿，
 * 但「+」和「−」谁都认得，所以正数必须显式带 `+`。
 * 负号用 U+2212（真正的减号），它在等宽数字里与加号同宽，整列不会错位。
 */
export function formatSigned(n: number): string {
  if (n === 0) return '0'
  return n > 0 ? `+${formatExact(n)}` : formatExact(n)
}

export type NetTone = 'up' | 'down' | 'flat'

export function netTone(n: number): NetTone {
  return n > 0 ? 'up' : n < 0 ? 'down' : 'flat'
}

/** 牌局时长。秒不给：一局打了 72 分 13 秒，那 13 秒没有任何人关心。 */
export function durationText(ms: number): string {
  const min = Math.floor(Math.max(0, ms) / 60_000)
  if (min < 1) return '不到 1 分'
  if (min < 60) return `${min} 分`
  const h = Math.floor(min / 60)
  const rest = min % 60
  return rest === 0 ? `${h} 小时` : `${h} 小时 ${rest} 分`
}

// ============================================================================
// 战报
// ============================================================================

export interface SettlementLine {
  /** React key。同一个人可能既在座又留着一行非零账本（服务端刻意两行都给） */
  readonly key: string
  readonly seat: SeatIndex | null
  readonly nickname: string
  readonly isBot: boolean
  /** 已离座：筹码托管在账本里，仍然要计入合计 */
  readonly left: boolean
  readonly buyIn: string
  readonly finalStack: string
  readonly net: string
  readonly tone: NetTone
  /** ★ 只在 > 0 时显示。给每一行都挂个「补买 0 次」是在制造噪音 */
  readonly topUpCount: number
}

export interface SettlementTotals {
  readonly buyIn: string
  readonly finalStack: string
  readonly net: string
  readonly tone: NetTone
}

export interface SettlementModel {
  /** 「牌局结束 · 47 手 · 1 小时 12 分」，读屏与单行场合用 */
  readonly headline: string
  /** 「47 手 · 1 小时 12 分」。截图里标题与它分两行，读起来才像一份战报 */
  readonly meta: string
  readonly rows: readonly SettlementLine[]
  readonly totals: SettlementTotals
  /** 筹码守恒。false = 有 bug，必须显眼示警，不是用户的错 */
  readonly balanced: boolean
}

/**
 * 战报 → 可直接渲染的行。
 *
 * 排序：在座的按座位号，离座的排在后面（他们已经不在牌桌上，混在中间会让人
 * 对着座位号找不到人）。★ 但**绝不能丢掉离座行** —— 他们的筹码托管在账本里，
 * 少算一边「Σ买入 == Σ最终筹码」这条守恒式会恒不成立。
 */
export function buildSettlementModel(report: SettlementReport): SettlementModel {
  const rows = [...report.rows]
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const sa = a.r.seat
      const sb = b.r.seat
      if (sa === null && sb === null) return a.i - b.i
      if (sa === null) return 1
      if (sb === null) return -1
      return sa - sb
    })
    .map(({ r, i }): SettlementLine => ({
      key: `${r.accountId}:${r.seat ?? 'off'}:${i}`,
      seat: r.seat,
      nickname: r.nickname === '' ? '（已退出）' : r.nickname,
      isBot: r.isBot,
      left: r.seat === null,
      buyIn: formatExact(r.buyInTotal),
      finalStack: formatExact(r.finalStack),
      net: formatSigned(r.net),
      tone: netTone(r.net),
      topUpCount: r.topUpCount,
    }))

  const net = report.totalFinal - report.totalBuyIn
  const meta = `${report.handsPlayed} 手 · ${durationText(report.endedAt - report.startedAt)}`
  return {
    headline: `牌局结束 · ${meta}`,
    meta,
    rows,
    totals: {
      buyIn: formatExact(report.totalBuyIn),
      finalStack: formatExact(report.totalFinal),
      net: formatSigned(net),
      tone: netTone(net),
    },
    balanced: report.balanced,
  }
}

/** 战报在不在手上。结算后房间会被关掉，所以它只能从快照 / `gameSettled` 里来。 */
export function settlementOf(view: PlayerView | null): SettlementReport | null {
  return view?.settlement ?? null
}
